import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { slotService } from "@/services/slotService";
import { format, parseISO } from "date-fns";
import { Loader2, Plus, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { SlotBookingForm } from "./SlotBookingForm";
import { toast } from "@/components/ui/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface SlotBookingsListProps {
  slotId: string;
  onUpdate: () => void;
}

export function SlotBookingsList({ slotId, onUpdate }: SlotBookingsListProps) {
  const [bookings, setBookings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    fetchBookings();
  }, [slotId]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const { data, error } = await slotService.getSlotBookings(slotId);
      if (error) throw error;
      setBookings(data || []);
    } catch (error) {
      console.error("Error fetching bookings:", error);
      toast({
        title: "Error",
        description: "Failed to load bookings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddBooking = async (bookingData: any) => {
    try {
      await slotService.createBooking(slotId, bookingData);
      setIsAddDialogOpen(false);
      fetchBookings();
      onUpdate();
      toast({
        title: "Booking added",
        description: "The booking has been added successfully",
      });
    } catch (error) {
      console.error("Error adding booking:", error);
      toast({
        title: "Error",
        description: "Failed to add booking",
        variant: "destructive",
      });
    }
  };

  const handleDeleteBooking = async (bookingId: string) => {
    try {
      setDeletingId(bookingId);
      await slotService.deleteBooking(bookingId);
      fetchBookings();
      onUpdate();
      toast({
        title: "Booking deleted",
        description: "The booking has been deleted successfully",
      });
    } catch (error) {
      console.error("Error deleting booking:", error);
      toast({
        title: "Error",
        description: "Failed to delete booking",
        variant: "destructive",
      });
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Bookings</h3>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Booking
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Booking</DialogTitle>
            </DialogHeader>
            <SlotBookingForm onSubmit={handleAddBooking} />
          </DialogContent>
        </Dialog>
      </div>

      {bookings.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No bookings for this slot yet.
        </div>
      ) : (
        <div className="space-y-2">
          {bookings.map((booking) => (
            <div
              key={booking.id}
              className="p-4 border rounded-md flex justify-between items-center"
            >
              <div>
                <div className="font-medium">
                  {booking.leads?.first_name
                    ? `${booking.leads.first_name} ${booking.leads.last_name}`
                    : booking.name || "Unnamed Booking"}
                </div>
                <div className="text-sm text-muted-foreground">
                  {booking.leads?.email || booking.email || "No email"}
                  {(booking.leads?.phone_number || booking.phone) && (
                    <span>
                      {" "}
                      • {booking.leads?.phone_number || booking.phone}
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Added on {format(parseISO(booking.created_at), "MMM d, yyyy")}
                </div>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={deletingId === booking.id}
                  >
                    {deletingId === booking.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4 text-destructive" />
                    )}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Booking</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete this booking? This action
                      cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => handleDeleteBooking(booking.id)}
                      className="bg-destructive text-destructive-foreground"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
